import { ProjectFields, YesObject } from '@yes.technology/react-toolkit'

const REGEX_VALUE_PATTERN = /(\{\{[^\}]+\}\})/g
const OBJECT_KEY_PATTERN = /[{}]/g

type ConcatenateArgs = {
  sourceMap: string
  object: YesObject
}

export const getObjectKey = (object: string) =>
  object.replace(OBJECT_KEY_PATTERN, '').split('.')

export const getProjectFields = (
  sourceMap: string
): ProjectFields | undefined => {
  const params = sourceMap.match(REGEX_VALUE_PATTERN)
  return params?.map(getObjectKey).reduce((accumulator, keys) => {
    const key = keys.join('.')
    return { ...accumulator, [key]: 1 }
  }, {})
}

const getValueFromObject = (object: YesObject, keys: string[]) => {
  const value = keys.reduce((currentValue: string | YesObject, key) => {
    if (typeof currentValue === 'string') {
      return currentValue
    }

    const nextValue = currentValue[key]

    if (Array.isArray(nextValue)) {
      return nextValue[0]
    }

    return nextValue || ''
  }, object)

  if (typeof value === 'string') {
    return value
  }

  return ''
}

export const concatenate = ({ sourceMap, object }: ConcatenateArgs) => {
  const params = sourceMap.match(REGEX_VALUE_PATTERN)

  if (!params) {
    return sourceMap
  }

  return params.reduce((accumulator, param) => {
    const objectKey = getObjectKey(param)
    const value = getValueFromObject(object, objectKey)

    return accumulator.replace(param, value)
  }, sourceMap)
}

export const matchesSourceMap = (sourceMap: string, value: string) => {
  if (typeof sourceMap !== 'string' || typeof value !== 'string') return false

  const tokenRegex = /(\{\{[^\}]+\}\})/g

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  let lastIndex = 0
  const parts: string[] = []
  let match: RegExpExecArray | null

  while ((match = tokenRegex.exec(sourceMap)) !== null) {
    const index = match.index
    const before = sourceMap.slice(lastIndex, index)
    if (before) parts.push(escapeRegExp(before))

    parts.push('([\\s\\S]+?)')

    lastIndex = index + match[0].length
  }

  const tail = sourceMap.slice(lastIndex)
  if (tail) parts.push(escapeRegExp(tail))

  const pattern = `^${parts.join('')}$`
  const re = new RegExp(pattern)

  return re.test(value)
}
